import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';
import { findGiftCardCandidates } from '../../utils/giftCardCandidates';
import { buildGiftCardPrintHtml } from '../../utils/giftCardPrintHtml';
import { buildEmbeddedGiftCardHtml } from '../../utils/printAssetInlining';
import { printHTMLSilent } from '../../utils/silentPrintService';
import {
  getQzPrintReadiness,
  loadQzPrintSettings,
  reconcileQzPrintSettings,
  saveQzPrintSettings,
  printHtmlBatchWithQz,
  withGiftCardTrayRotation,
} from '../../services/qzPrintService';
import {
  analyzePrintSeparatorGroups,
  buildPrintSeparatorHtml,
  groupItemsForPrintSeparators,
} from '../../utils/printJobSeparators';
import './GiftInscriptionPanel.css';
import QzPrinterSettingsPanel from './QzPrinterSettingsPanel';
import PrintJobStatusDialog from './PrintJobStatusDialog';
import { usePrintQueue } from '../../context/PrintQueueContext';

export default function GiftInscriptionPanel({
  orders = [],
  lookupOrder,
  initialLookupValue = '',
  title = 'Gift inscriptions',
  description = 'Review possible customer messages and print selected landscape A6 cards.',
}) {
  const printQueue = usePrintQueue();
  const [lookupValue, setLookupValue] = useState(() =>
    String(initialLookupValue || '').trim(),
  );
  const [lookupStatus, setLookupStatus] = useState('');
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupOrders, setLookupOrders] = useState([]);
  const [selected, setSelected] = useState({});
  const [messages, setMessages] = useState({});
  const [printing, setPrinting] = useState(false);
  const [printJobStatus, setPrintJobStatus] = useState(null);
  const [directPrint, setDirectPrint] = useState(false);
  const [includeSeparators, setIncludeSeparators] = useState(false);
  const [qzStatus, setQzStatus] = useState({
    ready: false,
    message: 'Checking QZ Tray…',
    printerDetails: [],
  });
  const [qzSettings, setQzSettings] = useState(() => loadQzPrintSettings());
  const autoLookupRef = useRef('');

  useEffect(() => {
    const next = String(initialLookupValue || '').trim();
    if (!next || next === autoLookupRef.current) return;
    setLookupValue(next);
  }, [initialLookupValue]);

  useEffect(() => {
    let active = true;
    getQzPrintReadiness({ force: true }).then((status) => {
      if (!active) return;
      setDirectPrint(status.ready);
      setQzStatus(status);
      if (status.ready) {
        setQzSettings((previous) => {
          const next = reconcileQzPrintSettings(previous, status.printerDetails);
          saveQzPrintSettings(next);
          return next;
        });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  const reviewOrders = useMemo(() => {
    const merged = [...orders, ...lookupOrders];
    const seen = new Set();
    return merged.filter((order) => {
      const key = String(
        order?.source_id || order?.sourceId || order?.orderId || order?.id || '',
      ).replace(/^#/, '');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [orders, lookupOrders]);

  const candidates = useMemo(
    () => findGiftCardCandidates(reviewOrders),
    [reviewOrders],
  );

  useEffect(() => {
    setSelected((previous) => {
      const next = {};
      candidates.forEach((candidate) => {
        next[candidate.id] =
          previous[candidate.id] == null
            ? candidate.likely
            : previous[candidate.id];
      });
      return next;
    });
    setMessages((previous) => {
      const next = {};
      candidates.forEach((candidate) => {
        next[candidate.id] =
          previous[candidate.id] == null
            ? candidate.message
            : previous[candidate.id];
      });
      return next;
    });
  }, [candidates]);

  const selectedCards = useMemo(
    () =>
      candidates
        .filter(
          (candidate) =>
            selected[candidate.id] &&
            String(messages[candidate.id] || '').trim(),
        )
        .map((candidate) => ({
          order: candidate.order,
          orderKey: candidate.orderKey,
          message: String(messages[candidate.id] || '').trim(),
          sourceLabel: candidate.sourceLabel,
        })),
    [candidates, selected, messages],
  );
  const selectedCardOrders = useMemo(
    () => selectedCards.map((card) => card.order).filter(Boolean),
    [selectedCards],
  );
  const separatorAnalysis = useMemo(
    () => analyzePrintSeparatorGroups(selectedCardOrders),
    [selectedCardOrders],
  );

  const handleLookup = async (forcedValue) => {
    const value = String(forcedValue ?? lookupValue).trim();
    if (!value || !lookupOrder) return;
    setLookupBusy(true);
    setLookupStatus('Looking up order…');
    try {
      const order = await lookupOrder(value);
      if (!order) {
        setLookupStatus('No order found for that identifier.');
        return;
      }
      setLookupOrders((previous) => [...previous, order]);
      const sourceLabel =
        order._inscriptionSource === 'orderDesk'
          ? 'OrderDesk'
          : order._inscriptionSource === 'local'
            ? 'loaded orders'
            : 'lookup';
      const candidateHint = findGiftCardCandidates([order]).length
        ? 'Review the candidates below.'
        : 'No note fields were found on that order — you can still type a message if needed.';
      setLookupStatus(
        `Loaded order ${value} from ${sourceLabel}. ${candidateHint}`,
      );
      setLookupValue('');
      autoLookupRef.current = value;
    } catch (error) {
      setLookupStatus(error?.message || 'Order lookup failed.');
    } finally {
      setLookupBusy(false);
    }
  };

  useEffect(() => {
    const next = String(initialLookupValue || '').trim();
    if (!next || !lookupOrder || next === autoLookupRef.current) return;
    handleLookup(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per new prefilled id
  }, [initialLookupValue, lookupOrder]);

  const handlePrint = async (queueOnly = false) => {
    if (!selectedCards.length || printing) return;
    setPrinting(true);
    try {
      if (queueOnly) {
        printQueue.enqueueGiftCards({
          cards: selectedCards,
          qzSettings,
          includeSeparators,
          source: 'inscriptions',
        });
        setLookupStatus(
          `${selectedCards.length} A6 card${selectedCards.length === 1 ? '' : 's'} added to the print queue.`,
        );
        return;
      }

      if (directPrint) {
        const qzJobs = [];
        if (includeSeparators) {
          const giftGroups = groupItemsForPrintSeparators(selectedCards);
          for (const group of giftGroups) {
            qzJobs.push({
              profileKey: 'giftCard',
              label: `A6 cards for ${group.shippingMethod}`,
              html: await buildEmbeddedGiftCardHtml(
                buildGiftCardPrintHtml,
                group.items,
              ),
              settings: withGiftCardTrayRotation(qzSettings.giftCard),
            });
            qzJobs.push({
              profileKey: 'separator',
              label: `Start of ${group.shippingMethod} (A6)`,
              html: buildPrintSeparatorHtml(group, {
                outputLabel: 'A6 inscription cards',
                formatLabel: 'A6 reverse landscape',
                segmentLabel: group.shippingMethod,
                orderCount: group.orders.length,
                labelCount: group.items.length,
                physicalSheetCount: group.items.length,
              }),
              settings: qzSettings.separator,
            });
          }
        } else {
          qzJobs.push({
            profileKey: 'giftCard',
            label: 'A6 inscription cards',
            html: await buildEmbeddedGiftCardHtml(
              buildGiftCardPrintHtml,
              selectedCards,
            ),
            settings: withGiftCardTrayRotation(qzSettings.giftCard),
          });
        }
        await printHtmlBatchWithQz(qzJobs);
        setLookupStatus(
          `${selectedCards.length} A6 card${selectedCards.length === 1 ? '' : 's'} sent directly to QZ (not added to the queue).`,
        );
        return;
      }

      const html = await buildEmbeddedGiftCardHtml(
        buildGiftCardPrintHtml,
        selectedCards,
        { rotateForTray: true },
      );
      await printHTMLSilent(html, { removeDelay: 1500 });
      setPrintJobStatus({
        submitted: selectedCards,
        message: 'The browser print dialog closed. Check the physical cards.',
      });
    } catch (error) {
      console.error('Gift inscription print failed:', error);
      if (error?.submissionStarted) {
        setPrintJobStatus({
          message: error.message,
          submitted: error.submitted || [],
          uncertain: error.uncertain || [],
          pending: error.pending || [],
        });
      } else {
        setLookupStatus(error?.message || 'Gift card printing failed.');
      }
    } finally {
      setPrinting(false);
    }
  };

  return (
    <section className="gift-inscription-panel">
      <header className="gift-inscription-panel__header">
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--primary"
          disabled={!selectedCards.length || printing}
          onClick={() => handlePrint(false)}
        >
          <PrinterOutlined />{' '}
          {printing
            ? 'Preparing…'
            : directPrint
              ? `Print ${selectedCards.length} A6 card${
                  selectedCards.length === 1 ? '' : 's'
                } now`
              : `Print ${selectedCards.length} A6 card${
                  selectedCards.length === 1 ? '' : 's'
                }`}
        </button>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--secondary"
          disabled={!selectedCards.length || printing}
          onClick={() => handlePrint(true)}
        >
          Add to print queue
        </button>
      </header>

      <label className="gift-inscription-panel__printer">
        <input
          type="checkbox"
          checked={directPrint}
          disabled={!qzStatus.ready}
          onChange={(event) => {
            setDirectPrint(event.target.checked);
            if (!event.target.checked) setIncludeSeparators(false);
          }}
        />
        <span>
          <b>Print directly with QZ Tray</b>
          <small>{qzStatus.message}</small>
          {qzStatus.connected && !qzStatus.signed && (
            <small>Unsigned local mode. Choose Allow and Remember when QZ asks.</small>
          )}
        </span>
      </label>
      {directPrint && separatorAnalysis.hasMultiple && (
        <label className="gift-inscription-panel__printer">
          <input
            type="checkbox"
            checked={includeSeparators}
            onChange={(event) => setIncludeSeparators(event.target.checked)}
          />
          <span>
            <b>Insert A4 Start-of segment sheets</b>
            <small>
              After each shipping / dispatch / delivery set: Start of cover with pick list,
              methods, and order count.
            </small>
          </span>
        </label>
      )}
      {directPrint && (
        <QzPrinterSettingsPanel
          profileKeys={[
            ...(includeSeparators ? ['separator'] : []),
            'giftCard',
          ]}
          printerDetails={qzStatus.printerDetails || []}
          settings={qzSettings}
          onChange={(next) => {
            setQzSettings(next);
            saveQzPrintSettings(next);
          }}
        />
      )}

      {lookupOrder && (
        <div className="gift-inscription-panel__lookup">
          <label htmlFor="gift-inscription-order-lookup">
            Order number or source ID
          </label>
          <div>
            <input
              id="gift-inscription-order-lookup"
              value={lookupValue}
              onChange={(event) => setLookupValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  handleLookup();
                }
              }}
              placeholder="For example 4898044312 or #9399"
            />
            <button
              type="button"
              className="toolkit-btn toolkit-btn--secondary"
              disabled={!lookupValue.trim() || lookupBusy}
              onClick={handleLookup}
            >
              <SearchOutlined /> {lookupBusy ? 'Looking…' : 'Find order'}
            </button>
          </div>
          {lookupStatus && <p>{lookupStatus}</p>}
        </div>
      )}

      {!candidates.length ? (
        <div className="gift-inscription-panel__empty">
          No possible inscription text is currently available. Search for an order or
          adjust the loaded order filters.
        </div>
      ) : (
        <div className="gift-inscription-panel__candidates">
          {candidates.map((candidate) => (
            <article
              key={candidate.id}
              className={`gift-inscription-candidate${
                selected[candidate.id] ? ' is-selected' : ''
              }`}
            >
              <label className="gift-inscription-candidate__heading">
                <input
                  type="checkbox"
                  checked={Boolean(selected[candidate.id])}
                  onChange={(event) =>
                    setSelected((previous) => ({
                      ...previous,
                      [candidate.id]: event.target.checked,
                    }))
                  }
                />
                <span>
                  <b>Order #{candidate.orderKey}</b>
                  <small>
                    {candidate.sourceLabel} ·{' '}
                    {candidate.likely ? 'Likely inscription' : 'Review manually'}
                  </small>
                </span>
              </label>
              <textarea
                rows="5"
                value={messages[candidate.id] || ''}
                onChange={(event) =>
                  setMessages((previous) => ({
                    ...previous,
                    [candidate.id]: event.target.value,
                  }))
                }
                aria-label={`Inscription for order ${candidate.orderKey}`}
              />
            </article>
          ))}
        </div>
      )}
      <PrintJobStatusDialog
        status={printJobStatus}
        busy={printing}
        onClose={() => setPrintJobStatus(null)}
        onRetryUnconfirmed={
          printJobStatus?.uncertain?.length || printJobStatus?.pending?.length
            ? async () => {
                setPrinting(true);
                try {
                  const jobs = [
                    ...(printJobStatus.uncertain || []),
                    ...(printJobStatus.pending || []),
                  ];
                  const submitted = await printHtmlBatchWithQz(jobs);
                  setPrintJobStatus({
                    submitted,
                    message: 'The selected A6 output was submitted to QZ. Check the cards.',
                  });
                } catch (error) {
                  setPrintJobStatus({
                    message: error.message,
                    submitted: error.submitted || [],
                    uncertain: error.uncertain || [],
                    pending: error.pending || [],
                  });
                } finally {
                  setPrinting(false);
                }
              }
            : undefined
        }
        onBrowserFallback={
          printJobStatus?.uncertain?.length || printJobStatus?.pending?.length
            ? async () => {
                setPrinting(true);
                try {
                  const jobs = [
                    ...(printJobStatus.uncertain || []),
                    ...(printJobStatus.pending || []),
                  ];
                  for (const job of jobs) {
                    await printHTMLSilent(job.html, { removeDelay: 1500 });
                  }
                  setPrintJobStatus({
                    submitted: jobs,
                    message: 'Browser dialogs closed. Check the physical cards.',
                  });
                } finally {
                  setPrinting(false);
                }
              }
            : undefined
        }
      />
    </section>
  );
}
